import classNames from "classnames";
import { camelizeKeys } from "humps";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import { useProfilePreview } from "PFApp/use_profile_preview";
import { Button } from "PFComponents/button";
import { ProfilesAvatarStack } from "PFComponents/profile_avatar/profiles_avatar_stack";
import { Typography } from "PFComponents/typography";
import { getProfileName } from "PFCore/helpers/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { PermissionRule, Profile, ProfileMinimized } from "PFTypes";
import { useCallback } from "react";

import css from "./activity_profiles_avatar_stack.module.scss";

type ActivityProfilesAvatarStackProps = {
  className?: string;
  profiles: Profile[];
  qaId?: string;
  label?: string;
  style?: React.CSSProperties;
  handleActionClick?: VoidFunction;
  handleIconClick?: (profile: Profile) => void;
  actionLabel?: string;
  displayChatIcon?: boolean;
  avatarSize?: number;
  max?: number;
};

export const ActivityProfilesAvatarStack = ({
  className,
  profiles,
  label,
  style,
  handleActionClick,
  actionLabel,
  displayChatIcon,
  handleIconClick,
  avatarSize,
  max,
  qaId
}: ActivityProfilesAvatarStackProps) => {
  const { data: currentProfile } = useCurrentProfile();

  const { showPreview } = useProfilePreview();

  const isChatEnabled = useIsChatEnabled();

  const title = label ? <Typography variant="h5">{label}:</Typography> : null;

  const handleAvatarClick = (profile: Profile) => showPreview({ id: profile.id, profile });

  const getTooltipContent = useCallback(
    (profile: Profile) => (
      <div className={css.tooltip}>
        <Typography withMargin variant="bodyBold" tag="span">
          {getProfileName(profile)}
        </Typography>
        <Typography withMargin variant="bodyRegular" tag="span">
          {profile.permissionsGroup.displayAs}
        </Typography>
      </div>
    ),
    []
  );

  const isProjectManager = (profile: Profile) =>
    !!profile.permissionsGroup.permissions.find(
      ({ name, permitted }) => name === PermissionRule.ProjectManager && permitted
    );

  return (
    <div style={style} className={classNames(css.avatar, className)} data-qa-id={qaId}>
      {handleActionClick ? (
        <div className={css.actionWrapper}>
          {title}
          <Button
            text={actionLabel || ""}
            onClick={handleActionClick}
            className={css.actionButton}
            small
            kind="secondary"
          />
        </div>
      ) : (
        title
      )}
      {/* @ts-ignore */}
      <ProfilesAvatarStack
        profiles={camelizeKeys(profiles) as ProfileMinimized[]}
        max={max}
        avatarSize={avatarSize}
        handleAvatarClick={handleAvatarClick}
        icon={isChatEnabled && displayChatIcon ? "chat" : undefined}
        handleIconClick={handleIconClick}
        currentProfile={currentProfile}
        getTooltipContent={getTooltipContent}
        getIsAvatarHighlighted={isProjectManager}
      />
    </div>
  );
};
